import { setIn } from 'timm';
import { call } from 'typed-redux-saga';
import {
  AsyncActionState,
  asyncActionStateMatchers,
  setupSlice,
  invoke,
} from '@owl-frontend/redux';
import templatesApiClient from '../../../../api/templates/client';
import type { ClaimTemplate } from '../../../../api/templates/interface';
import type { ShellTemplateItem, ShellTemplateState } from './interface';

export interface OwlShellTemplatesState {
  claimTemplates: {
    [dossierId: string]: {
      [templateType: string]: ShellTemplateItem[];
    };
  };
  claimTemplateDetails: {
    [dossier_id: string]: {
      [template_id: string]: ShellTemplateState;
    };
  };
  actions: {
    fetchClaimTemplates: AsyncActionState;
    fetchClaimTemplateState: AsyncActionState<ClaimTemplate[]>;
    fetchClaimTemplateDetails: AsyncActionState<string>;
    downloadTemplate: AsyncActionState;
    createTemplate: AsyncActionState;
    restoreTemplateVersion: AsyncActionState;
  };
}

const initialState: OwlShellTemplatesState = {
  claimTemplates: {},
  claimTemplateDetails: {},
  actions: {
    fetchClaimTemplates: { status: 'uninitialized' },
    fetchClaimTemplateState: { status: 'uninitialized' },
    fetchClaimTemplateDetails: { status: 'uninitialized' },
    downloadTemplate: { status: 'uninitialized' },
    createTemplate: { status: 'uninitialized' },
    restoreTemplateVersion: { status: 'uninitialized' },
  },
};

function* downloadTemplate(action: {
  payload: {
    dossier_id: string;
    template_id: string;
    template_type: string;
    client_dossier_id: string;
    template_version_id?: string;
  };
}) {
  const {
    dossier_id,
    template_id,
    template_type,
    client_dossier_id,
    template_version_id,
  } = action.payload;
  const response = yield* call(templatesApiClient.downloadHtmlTemplateAsRtf, {
    dossierId: dossier_id,
    templateId: template_id,
    template_version_id,
  });

  const blob = new Blob([response.rtf_content || ''], {
    type: 'application/rtf',
  });
  const blobUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = blobUrl;
  link.download = `${client_dossier_id}-${template_type}.rtf`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(blobUrl);
}

const slice = setupSlice('owl-shell-templates', initialState)
  .addAsyncSagas({
    fetchClaimTemplates: invoke(templatesApiClient.fetch),
    fetchClaimTemplateState: invoke(templatesApiClient.fetchTemplateState),
    fetchClaimTemplateDetails: invoke(templatesApiClient.fetchDetail),
    downloadTemplate,
    createTemplate: invoke(templatesApiClient.create),
    restoreTemplateVersion: invoke(templatesApiClient.restoreTemplateVersion),
  })
  .addReducers({
    'fetchClaimTemplates/fulfilled': (state, action) => {
      const { dossier_id, template_type } = action.meta.arg;
      if (template_type) {
        state.claimTemplates = setIn(
          state.claimTemplates,
          [dossier_id, template_type],
          action.payload.templates
        ) as typeof state.claimTemplates;
      }
    },
    'fetchClaimTemplateState/fulfilled': (state, action) => {
      const { dossier_id, template_id } = action.meta.arg;
      state.claimTemplateDetails = setIn(
        state.claimTemplateDetails,
        [dossier_id, template_id],
        action.payload
      ) as typeof state.claimTemplateDetails;
    },
    'fetchClaimTemplateDetails/fulfilled': (state, action) => {
      const { dossier_id, template_id } = action.meta.arg;
      state.claimTemplateDetails = setIn(
        state.claimTemplateDetails,
        [dossier_id, template_id],
        {
          format: 'html',
          versions: [
            {
              html: action.payload,
              template_version_id: template_id,
            },
          ],
        }
      ) as typeof state.claimTemplateDetails;
    },
    'restoreTemplateVersion/fulfilled': (state, action) => {
      const { dossier_id, template_id } = action.meta.arg;
      const existing = state.claimTemplateDetails?.[dossier_id]?.[template_id];

      const prevVersions = existing?.versions ?? [];
      state.claimTemplateDetails = setIn(
        state.claimTemplateDetails,
        [dossier_id, template_id],
        {
          ...existing,
          versions: [action.payload.template_version, ...prevVersions],
        }
      ) as typeof state.claimTemplateDetails;
    },
  });

export const actions = slice.actions;
export default slice.addExtra(asyncActionStateMatchers(actions).all());
